// ═══════════════════════════════════════════════════════════════════════
// PERSONNELMANAGER ENRICHI COMPLET - VERSION PRODUCTION FINALE
// ═══════════════════════════════════════════════════════════════════════
// Modèle: User.js - Staff = Users avec role admin/instructeur/moderator

import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Edit,
  Eye,
  Filter,
  Key,
  Mail,
  Plus,
  Save,
  Search,
  Trash2,
  X
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useGrandeEcole from '../../stores/useGrandeEcole';

export function PersonnelManager({ darkMode = true, isEditable = false }) {
  const {
    staff = [],
    loading,
    updateStaff,
    createStaff,
    deleteStaff,
    openCompose
  } = useGrandeEcole();

  // ═══════════════════════════════════════════════════════════════════════
  // ÉTATS
  // ═══════════════════════════════════════════════════════════════════════

  const [search, setSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    pesaRole: 'all',
    status: 'all',
    role: 'all'  // role système (lecture seule)
  });

  // Édition COMPLÈTE
  const [editedStaff, setEditedStaff] = useState({});
  
  // Formulaire nouveau membre
  const [showNewForm, setShowNewForm] = useState(false);
  const [newMember, setNewMember] = useState({
    nom: '',
    prenom: '',
    email: '',
    phoneNumber: '',
    pesaRole: 'Formateur',   // titre/poste (affiché)
    status: 'active',
    photoUrl: ''
  });

  // Sélection multiple
  const [selectedStaff, setSelectedStaff] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');

  // Modals
  const [detailModal, setDetailModal] = useState(null);
  const [permissionsModal, setPermissionsModal] = useState(null);

  const PESA_ROLE = [    
    "Président",
    "Administrateur",
    "Facilitateur",
    "Ingénieur",
    "Directeur",
    "Médiateur",
    "Formateur",
    "Instructeur",
    "Editeur",
    "Modérateur",
    "Support",
  ];

  const SYSTEM_ROLES = [
    { value: 'admin', label: 'Admin (accès complet)' },
    { value: 'staff', label: 'Membre (accès GrandeÉcole)' },
    
  ];

  const STATUS_OPTIONS = ['active', 'inactive', 'suspended', 'actif', 'inactif', 'suspendu'];

  const formatLastLogin = (date) => {
    if (!date) return '—';
    try {
      const parsed = typeof date === 'string' ? parseISO(date) : date;
      return format(parsed, 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return date.toString();
    }
  };

  // Filtrage
  const filteredStaff = useMemo(() => {
    let result = [...staff];

    // Recherche texte
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(s =>
        `${s.nom || ''} ${s.prenom || ''}`.toLowerCase().includes(searchLower) ||
        s.email?.toLowerCase().includes(searchLower) ||
        s.pesaRole?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre pesaRole (titre/poste)
    if (filters.pesaRole !== 'all') {
      result = result.filter(s => s.pesaRole === filters.pesaRole);
    }

    // Filtre status
    if (filters.status !== 'all') {
      result = result.filter(s => s.status === filters.status);
    }

    // Filtre role système
    if (filters.role !== 'all') {
      result = result.filter(s => s.role === filters.role);
    }

    return result;
  }, [staff, search, filters]);


  const startEditMember = (member) => {
    const id = member._id || member.id;
    if (!id) return;

    setEditedStaff(prev => ({
      ...prev,
      [id]: {
        ...member,
        nom:         member.nom || member.lastName  || '',
        prenom:      member.prenom || member.firstName || '',
        email:       member.email || '',
        phoneNumber: member.phoneNumber || '',
        pesaRole:    member.pesaRole || '',
        status:      member.status || 'active',
        photoUrl:    member.photoUrl || '',
        notes:       member.notes || '',
        cohorte:     member.cohorte?._id || member.cohorteId || '',
      }
    }));
  };

  const updateEditedField = (id, field, value) => {
    setEditedStaff(prev => {
      const current = prev[id];

      let validated = value;

      if (field === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          validated = { value, invalid: true };
        }
      }

      if (field === 'phoneNumber') {
        validated = value.replace(/[^\d+\s()-]/g, '');
      }

      return {
        ...prev,
        [id]: { ...current, [field]: validated }
      };
    });
  };
  
  const saveMember = async (id) => {
    const edited = editedStaff[id];
    if (!edited) return;

    const errors = [];
    if (!edited.nom)    errors.push('Nom obligatoire');
    if (!edited.prenom) errors.push('Prénom obligatoire');
    if (!edited.email)  errors.push('Email obligatoire');
    if (edited.email?.invalid) errors.push('Email invalide');
    if (!edited.pesaRole) errors.push('Poste obligatoire');

    if (errors.length > 0) {
      toast.error(`Erreurs de validation :\n- ${errors.join('\n- ')}`);
      return;
    }

    const confirm = window.confirm(
      `Sauvegarder les modifications ?\n\n` +
      `Nom : ${edited.nom} ${edited.prenom}\n` +
      `Email : ${edited.email}\n` +
      `Poste : ${edited.pesaRole}\n` +
      `Status : ${edited.status}`
    );

    if (!confirm) return;

    try {
      const response = await updateStaff(id, {
        nom:         edited.nom,
        prenom:      edited.prenom,
        email:       edited.email,
        phoneNumber: edited.phoneNumber,
        pesaRole:    edited.pesaRole,
        status:      edited.status,
        photoUrl:    edited.photoUrl,
        notes:       edited.notes,
        cohorte:     edited.cohorte,
        // role intentionnellement absent
      });

      if (!response.success) {
        toast.error(`❌ Erreur : ${response.error}`);
        return;
      }

      setEditedStaff(prev => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });

      toast.success('Membre mis à jour avec succès');
    } catch (err) {
      console.error('Erreur sauvegarde membre:', err);
      toast.error(`❌ Erreur : ${err.message}`);
    }
  };
  
  const cancelEditMember = (id) => {
    setEditedStaff(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const saveNewMember = async () => {
    if (!newMember.nom || !newMember.prenom || !newMember.email || !newMember.phoneNumber) {
      toast.error('Nom, prénom, email et téléphone obligatoires');
      return;
    }
    if (!newMember.pesaRole) {
      toast.error('Le poste est obligatoire');
      return;
    }

    try {
      const result = await createStaff({
        ...newMember,
        generateTempPassword: true
      });

      if (!result.success) {
        toast.error(`❌ Erreur : ${result.error}`);
        return;
      }

      setNewMember({
        nom: '', prenom: '', email: '', phoneNumber: '',
        pesaRole: 'Formateur', status: 'active', photoUrl: ''
      });
      setShowNewForm(false);

      toast.success(
        `Membre créé\nMot de passe temporaire : ${result.tempPassword || 'Envoyé dans la messagerie'}`
      );
    } catch (err) {
      toast.error(`❌ ${err.message}`);
    }
  };

  const handleNewChange = (field, value) => {
    setNewMember(prev => ({ ...prev, [field]: value }));
  };
  

  const toggleSelectMember = (id) => {
    setSelectedStaff(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllMembers = () => {
    setSelectedStaff(new Set(filteredStaff.map(s => s._id || s.id)));
  };

  const clearSelection = () => setSelectedStaff(new Set());

  const handleBulkAction = async () => {
    if (selectedStaff.size === 0 || !bulkAction) {
      toast.error('Sélectionnez des membres et une action');
      return;
    }

    const ids = Array.from(selectedStaff);

    switch (bulkAction) {
      case 'change-status':
        const newStatus = prompt('Nouveau status (active, inactive, suspended) :');
        if (!newStatus || !STATUS_OPTIONS.includes(newStatus)) {
          toast.error('Status invalide');
          return;
        }
        // TODO: bulkUpdateStaff
        toast.info('Fonction en développement');
        break;

      case 'delete-selected':
        if (!window.confirm(`⚠️ Supprimer ${ids.length} membre(s) ?`)) return;
        const code = prompt('Tapez "SUPPRIMER" :');
        if (code !== 'SUPPRIMER') {
          toast.info('Annulé');
          return;
        }

        try {
          for (const id of ids) await deleteStaff(id);
          toast.success(`${ids.length} membres supprimés`);
          clearSelection();
        } catch (err) {
          toast.error(`❌ ${err.message}`);
        }
        break;

      default:
        toast.info('Action non implémentée');
    }

    setBulkAction('');
  };

  const openDetailModal = (member) => setDetailModal(member);
  const closeDetailModal = () => setDetailModal(null);


  const actifs = staff.filter(p => p.status === 'active').length;

  if (loading) {
    return <div className="h-64 bg-zinc-800 animate-pulse rounded" />;
  }

  return (
    <div className="space-y-8 relative">
      {/* HEADER */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-zinc-800 pb-4 gap-4">
          <div>
            <h3 className="text-2xl font-black tracking-tighter text-sky-400 uppercase">
              Équipe Opérationnelle
            </h3>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
              {filteredStaff.length} / {staff.length} affichés – {actifs} en poste actif
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Recherche */}
            <div className="relative flex-1 md:flex-none">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher nom, email..."
                className="w-full md:w-64 pl-10 pr-4 py-2 text-sm font-mono bg-zinc-900/50 border border-zinc-800 text-zinc-200 placeholder-zinc-600 outline-none focus:border-sky-500/50 rounded-none"
              />
            </div>

            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`px-4 py-2 text-xs font-bold uppercase border transition-all ${
                showAdvancedFilters
                  ? 'bg-sky-600/30 border-sky-500/50 text-sky-300'
                  : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Filter size={14} className="inline mr-2" />
              Filtres
            </button>

            <button
              onClick={() => setShowNewForm(!showNewForm)}
              className="px-6 py-2.5 bg-sky-600/20 hover:bg-sky-600/40 border border-sky-500/30 text-sky-300 font-medium text-sm transition-colors flex items-center gap-2"
            >
              <Plus size={14} />
              Ajouter membre
            </button>
          </div>
        </div>

        {/* FILTRES AVANCÉS */}
        {showAdvancedFilters && (
          <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded space-y-4 animate-in fade-in slide-in-from-top-2">
            <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Filtres Avancés</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">Poste (pesaRole)</label>
                <select
                  value={filters.pesaRole}
                  onChange={e => setFilters({ ...filters, pesaRole: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
                >
                  <option value="all">Tous</option>
                  {PESA_ROLE.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">Statut</label>
                <select
                  value={filters.status}
                  onChange={e => setFilters({ ...filters, status: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
                >
                  <option value="all">Tous</option>
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">Role système (lecture)</label>
                <select
                  value={filters.role}
                  onChange={e => setFilters({ ...filters, role: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
                >
                  <option value="all">Tous</option>
                  {SYSTEM_ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setFilters({ pesaRole: 'all', status: 'all', role: 'all' })}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs"
              >
                Réinitialiser
              </button>
              <button
                onClick={() => setShowAdvancedFilters(false)}
                className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}

        {/* BARRE BULK */}
        {selectedStaff.size > 0 && (
          <div className="bg-sky-950/20 border border-sky-800/50 p-4 rounded flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sky-300 font-bold">
                {selectedStaff.size} sélectionné(s)
              </span>
              <select
                value={bulkAction}
                onChange={e => setBulkAction(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              >
                <option value="">Choisir une action...</option>
                <option value="change-status">Changer le status</option>
                <option value="delete-selected">❌ Supprimer</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className="px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-xs font-medium"
              >
                Exécuter
              </button>
              <button
                onClick={selectAllMembers}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs"
              >
                Tout sélectionner
              </button>
              <button
                onClick={clearSelection}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs"
              >
                Désélectionner
              </button>
            </div>
          </div>
        )}
      </div>

      {/* FORMULAIRE NOUVEAU MEMBRE */}
      {showNewForm && (
        <div className="border border-sky-800/50 bg-sky-950/20 p-6 rounded space-y-6">
          <h4 className="text-lg font-black text-sky-300">Ajouter un membre à l'équipe</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">Nom *</label>
              <input
                type="text"
                value={newMember.nom}
                onChange={e => handleNewChange('nom', e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">Prénom *</label>
              <input
                type="text"
                value={newMember.prenom}
                onChange={e => handleNewChange('prenom', e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">Email *</label>
              <input
                type="email"
                value={newMember.email}
                onChange={e => handleNewChange('email', e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">Téléphone *</label>
              <input
                type="tel"
                value={newMember.phoneNumber}
                onChange={e => handleNewChange('phoneNumber', e.target.value)}
                placeholder="+33612345678"
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">
                Role système
              </label>
              <div className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm text-zinc-500 flex items-center gap-2">
                <span className="inline-flex px-2 py-0.5 text-[9px] font-black rounded uppercase border
                  bg-zinc-800 text-zinc-400 border-zinc-700">
                  staff
                </span>
                <span className="text-[11px] text-zinc-600">Accès GrandeÉcole partagé — défini automatiquement</span>
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">
                Poste * <span className="text-[9px] text-zinc-600">(affiché)</span>
              </label>
              <select
                value={newMember.pesaRole}
                onChange={e => handleNewChange('pesaRole', e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              >
                {PESA_ROLE.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">Status</label>
              <select
                value={newMember.status}
                onChange={e => handleNewChange('status', e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] uppercase text-zinc-400 mb-1">Photo URL</label>
              <input
                type="url"
                value={newMember.photoUrl}
                onChange={e => handleNewChange('photoUrl', e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              />
            </div>
          </div>

          <div className="bg-amber-950/20 border border-amber-800/50 p-3 rounded">
            <div className="flex items-start gap-2 text-amber-300 text-xs">
              <Key size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>Mot de passe temporaire :</strong> Un mot de passe sécurisé sera généré automatiquement et envoyé dans votre messagerie interne. Vous pourrez ensuite les communiquer
                au nouveau membre.
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowNewForm(false)}
              className="px-5 py-2 bg-zinc-800 border border-zinc-600 text-zinc-300 text-sm"
            >
              Annuler
            </button>
            <button
              onClick={saveNewMember}
              className="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm flex items-center gap-2"
            >
              <Plus size={14} />
              Créer le compte
            </button>
          </div>
        </div>
      )}

      {/* TABLEAU */}
      <div className="border border-zinc-800 overflow-hidden bg-black/40">

  {/* Header — desktop uniquement */}
  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] font-black uppercase tracking-widest border-b bg-zinc-900/60 border-zinc-700 text-zinc-400">
    <div className="col-span-1 flex items-center justify-center">
      <input type="checkbox"
        checked={selectedStaff.size === filteredStaff.length && filteredStaff.length > 0}
        onChange={e => e.target.checked ? selectAllMembers() : clearSelection()}
        className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-sky-500" />
    </div>
    <div className="col-span-3">IDENTITÉ</div>
    <div className="col-span-2">POSTE</div>
    <div className="col-span-1">ROLE SYS</div>
    <div className="col-span-2">STATUT</div>
    <div className="col-span-1">DERN. CONNEXION</div>
    <div className="col-span-2 text-right">ACTIONS</div>
  </div>

  {/* Rows */}
  <div className="divide-y divide-zinc-800/40 max-h-[600px] overflow-y-auto">
    {filteredStaff.length > 0 ? (
      filteredStaff.map(p => {
        const id = p._id || p.id;
        const isEdited = !!editedStaff[id];
        const isSelected = selectedStaff.has(id);
        const current = isEdited ? editedStaff[id] : p;

        return (
          <div key={id} className={`transition-colors ${
            isSelected ? 'bg-sky-950/20 border-l-2 border-sky-500' : 'hover:bg-sky-950/10'
          }`}>

            {/* ═══════════════════════════════════ */}
            {/* VUE MOBILE : carte compacte         */}
            {/* ═══════════════════════════════════ */}
            <div className="md:hidden px-4 py-3 space-y-2.5">

              {/* Ligne 1 : checkbox + avatar + nom + role badge */}
              <div className="flex items-center gap-2.5">
                <input type="checkbox" checked={isSelected} onChange={() => toggleSelectMember(id)}
                  className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-sky-500 shrink-0" />
                <div className="w-8 h-8 shrink-0 rounded bg-sky-900/30 flex items-center justify-center text-sky-400 font-black border border-sky-500/30 text-xs">
                  {((current.nom || '') + (current.prenom || '')).slice(0, 2).toUpperCase() || '??'}
                </div>
                <div className="flex-1 min-w-0">
                  {isEdited ? (
                    <div className="space-y-1">
                      <input type="text" value={current.nom} onChange={e => updateEditedField(id, 'nom', e.target.value)}
                        placeholder="Nom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-sky-500 rounded" />
                      <input type="text" value={current.prenom} onChange={e => updateEditedField(id, 'prenom', e.target.value)}
                        placeholder="Prénom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-sky-500 rounded" />
                      <input type="email" value={current.email} onChange={e => updateEditedField(id, 'email', e.target.value)}
                        placeholder="Email" className={`w-full bg-zinc-900 border px-2 py-1 text-xs outline-none focus:border-sky-500 rounded ${current.email?.invalid ? 'border-red-500' : 'border-zinc-700'}`} />
                    </div>
                  ) : (
                    <>
                      <div className="font-bold text-sm truncate">{current.nom} {current.prenom}</div>
                      <div className="text-[10px] text-zinc-500 font-mono truncate">{current.email}</div>
                    </>
                  )}
                </div>
                <span className={`inline-flex px-2 py-0.5 text-[9px] font-black rounded uppercase border ${
                  current.role === 'admin'
                    ? 'bg-purple-900/20 text-purple-400 border-purple-500/30'
                    : 'bg-zinc-800 text-zinc-500 border-zinc-700'  // staff et tout le reste
                }`}>
                  {current.role}
                </span>
              </div>

              {/* Ligne 2 : poste + status + dernière connexion */}
              <div className="flex items-center gap-2.5 pl-[52px] flex-wrap">
                {isEdited ? (
                  <select value={current.pesaRole || ''} onChange={e => updateEditedField(id, 'pesaRole', e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs outline-none focus:border-sky-500 rounded">
                    {PESA_ROLE.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <span className="text-sky-300 font-medium text-xs truncate">
                    {current.pesaRole || current.role || '—'}
                  </span>
                )}
                <span className="text-zinc-800">·</span>
                {isEdited ? (
                  <select value={current.status} onChange={e => updateEditedField(id, 'status', e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs outline-none focus:border-sky-500 rounded">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <span className={`inline-flex px-2 py-0.5 text-[9px] font-bold rounded uppercase border ${
                    current.status === 'active'    ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40'
                    : current.status === 'suspended' ? 'bg-red-900/40 text-red-300 border-red-700/40'
                    : 'bg-amber-900/40 text-amber-300 border-amber-700/40'
                  }`}>{current.status}</span>
                )}
                <span className="text-zinc-600 font-mono text-[10px] ml-auto">
                  {formatLastLogin(current.lastLogin || current.dernierLogin)}
                </span>
              </div>

              {/* Ligne 3 : actions */}
              <div className="flex items-center justify-end gap-1 pl-[52px]">
                {isEdited ? (
                  <>
                    <button onClick={() => saveMember(id)} className="p-2 hover:text-sky-400 transition-colors" title="Sauvegarder"><Save size={14} /></button>
                    <button onClick={() => cancelEditMember(id)} className="p-2 hover:text-red-400 transition-colors" title="Annuler"><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => openDetailModal(p)} className="p-2 hover:text-sky-400 transition-colors" title="Voir détails"><Eye size={14} /></button>
                    <button onClick={() => startEditMember(p)} disabled={!isEditable}
                      className={`p-2 transition-colors ${isEditable ? 'hover:text-sky-400' : 'opacity-40 cursor-not-allowed'}`}
                      title={isEditable ? "Éditer" : "Activez Mode Lab"}><Edit size={14} /></button>
                    <button onClick={() => openCompose({ id: p._id || p.id, name: `${p.nom} ${p.prenom}`, email: p.email })}
                      className="p-2 hover:text-sky-400 transition-colors" title="Contacter"><Mail size={14} /></button>
                    <button onClick={() => { if (window.confirm('Supprimer ce membre ?')) deleteStaff(id); }}
                      disabled={!isEditable}
                      className={`p-2 transition-colors ${isEditable ? 'hover:text-red-500' : 'opacity-40 cursor-not-allowed'}`}
                      title={isEditable ? "Supprimer" : "Activez Mode Lab"}><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            </div>

            {/* ═══════════════════════════════════ */}
            {/* VUE DESKTOP md+ : tableau 12 col    */}
            {/* ═══════════════════════════════════ */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">

              {/* CHECKBOX */}
              <div className="col-span-1 flex items-center justify-center">
                <input type="checkbox" checked={isSelected} onChange={() => toggleSelectMember(id)}
                  className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-sky-500" />
              </div>

              {/* IDENTITÉ */}
              <div className="col-span-3 flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 rounded bg-sky-900/30 flex items-center justify-center text-sky-400 font-black border border-sky-500/30">
                  {((current.nom || '') + (current.prenom || '')).slice(0, 2).toUpperCase() || '??'}
                </div>
                <div className="flex-1 min-w-0">
                  {isEdited ? (
                    <div className="space-y-1">
                      <input type="text" value={current.nom} onChange={e => updateEditedField(id, 'nom', e.target.value)}
                        placeholder="Nom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-sky-500 rounded" />
                      <input type="text" value={current.prenom} onChange={e => updateEditedField(id, 'prenom', e.target.value)}
                        placeholder="Prénom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-sky-500 rounded" />
                      <input type="email" value={current.email} onChange={e => updateEditedField(id, 'email', e.target.value)}
                        placeholder="Email" className={`w-full bg-zinc-900 border px-2 py-1 text-xs outline-none focus:border-sky-500 rounded ${current.email?.invalid ? 'border-red-500' : 'border-zinc-700'}`} />
                    </div>
                  ) : (
                    <>
                      <div className="font-bold text-sm truncate">{current.nom} {current.prenom}</div>
                      <div className="text-[10px] text-zinc-500 font-mono truncate">{current.email}</div>
                    </>
                  )}
                </div>
              </div>

              {/* POSTE */}
              <div className="col-span-2">
                {isEdited ? (
                  <select value={current.pesaRole || ''} onChange={e => updateEditedField(id, 'pesaRole', e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-sky-500 rounded">
                    {PESA_ROLE.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <div className="text-sky-300 font-medium text-sm">{current.pesaRole || current.role || '—'}</div>
                )}
              </div>

              {/* ROLE SYSTÈME */}
              <div className="col-span-1">
                <span className={`inline-flex px-2 py-0.5 text-[9px] font-black rounded uppercase border ${
                  current.role === 'admin'       ? 'bg-purple-900/20 text-purple-400 border-purple-500/30'
                  : current.role === 'instructeur' ? 'bg-sky-900/20 text-sky-400 border-sky-500/30'
                  : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                }`}>{current.role}</span>
              </div>

              {/* STATUS */}
              <div className="col-span-2">
                {isEdited ? (
                  <select value={current.status} onChange={e => updateEditedField(id, 'status', e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-sky-500 rounded">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <span className={`inline-flex px-3 py-1 text-[10px] font-bold rounded uppercase border ${
                    current.status === 'active'    ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40'
                    : current.status === 'suspended' ? 'bg-red-900/40 text-red-300 border-red-700/40'
                    : 'bg-amber-900/40 text-amber-300 border-amber-700/40'
                  }`}>{current.status}</span>
                )}
              </div>

              {/* DERNIÈRE CONNEXION */}
              <div className="col-span-1 text-zinc-400 font-mono text-[11px]">
                {formatLastLogin(current.lastLogin || current.dernierLogin)}
              </div>

              {/* ACTIONS */}
              <div className="col-span-2 flex items-center justify-end gap-2 opacity-70 hover:opacity-100 transition-opacity">
                {isEdited ? (
                  <>
                    <button onClick={() => saveMember(id)} className="p-1.5 hover:text-sky-400 transition-colors" title="Sauvegarder"><Save size={14} /></button>
                    <button onClick={() => cancelEditMember(id)} className="p-1.5 hover:text-red-400 transition-colors" title="Annuler"><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => openDetailModal(p)} className="p-1.5 hover:text-sky-400 transition-colors" title="Voir détails"><Eye size={14} /></button>
                    <button onClick={() => startEditMember(p)} disabled={!isEditable}
                      className={`p-1.5 transition-colors ${isEditable ? 'hover:text-sky-400 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                      title={isEditable ? "Éditer membre" : "Activez Mode Lab"}><Edit size={14} /></button>
                    <button onClick={() => openCompose({ id: p._id || p.id, name: `${p.nom} ${p.prenom}`, email: p.email })}
                      className="p-1.5 hover:text-sky-400 transition-colors" title="Contacter"><Mail size={14} /></button>
                    <button onClick={() => { if (window.confirm('Supprimer ce membre ?')) deleteStaff(id); }}
                      disabled={!isEditable}
                      className={`p-1.5 transition-colors ${isEditable ? 'hover:text-red-500 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                      title={isEditable ? "Supprimer membre" : "Activez Mode Lab"}><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            </div>

          </div>
        );
      })
    ) : (
      <div className="py-20 text-center text-zinc-600 font-mono text-xs uppercase tracking-widest">
        Aucun membre détecté
      </div>
    )}
  </div>
</div>

      {/* MODAL DÉTAILS */}
      {detailModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-3xl max-h-[90vh] bg-zinc-900 border border-zinc-700 shadow-2xl rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-950">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded bg-sky-900/30 flex items-center justify-center text-sky-400 font-black border border-sky-500/30 text-lg">
                  {((detailModal.nom || '') + (detailModal.prenom || '')).slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-sky-400">
                    {detailModal.nom} {detailModal.prenom}
                  </h3>
                  <p className="text-sm text-zinc-500">{detailModal.email}</p>
                </div>
              </div>
              <button
                onClick={closeDetailModal}
                className="p-2 hover:bg-zinc-800 rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Infos personnelles */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                    Informations
                  </h4>
                  
                  <div className="space-y-3 text-sm">
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Email</label>
                      <div className="text-zinc-300">{detailModal.email}</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Téléphone</label>
                      <div className="text-zinc-300 font-mono">{detailModal.phoneNumber || '—'}</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Poste</label>
                      <div className="text-sky-300 font-bold">{detailModal.pesaRole || detailModal.role}</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Role système</label>
                      <div className="text-zinc-300">
                        <span className={`inline-flex px-2 py-0.5 text-[9px] font-black rounded uppercase border ${
                          detailModal.role === 'admin'
                            ? 'bg-purple-900/20 text-purple-400 border-purple-500/30'
                            : 'bg-sky-900/20 text-sky-400 border-sky-500/30'
                        }`}>
                          {detailModal.role}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-emerald-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                    Activité
                  </h4>
                  
                  <div className="space-y-3 text-sm">
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Status</label>
                      <div>
                        <span className={`inline-flex px-3 py-1 text-[10px] font-bold rounded uppercase border ${
                          detailModal.status === 'active'
                            ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40'
                            : 'bg-amber-900/40 text-amber-300 border-amber-700/40'
                        }`}>
                          {detailModal.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Dernière connexion</label>
                      <div className="text-zinc-300 font-mono text-sm">
                        {formatLastLogin(detailModal.lastLogin || detailModal.dernierLogin)}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Membre depuis</label>
                      <div className="text-zinc-300">
                        {detailModal.createdAt ? format(new Date(detailModal.createdAt), 'dd MMMM yyyy', { locale: fr }) : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 pt-6 border-t border-zinc-800 flex justify-end gap-3">
                <button
                  onClick={() => {
                    startEditMember(detailModal);
                    closeDetailModal();
                  }}
                  className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded flex items-center gap-2"
                >
                  <Edit size={14} />
                  Éditer
                </button>
                <button
                  onClick={() => {
                    openCompose({
                      id: detailModal._id || detailModal.id,
                      name: `${detailModal.nom} ${detailModal.prenom}`,
                      email: detailModal.email
                    });
                    closeDetailModal();
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded flex items-center gap-2"
                >
                  <Mail size={14} />
                  Contacter
                </button>
                <button
                  onClick={closeDetailModal}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-sm rounded"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}